import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SOLUTIONS } from "@/data/content";
import { MaskLines } from "@/components/site/Reveal";

// Bento layout: 12 cols, rows aligned (image tiles span 2 rows).
const LAYOUT = {
  "managed-it": "lg:col-span-7 lg:row-span-2",
  dispatch: "lg:col-span-5",
  "onsite-support": "lg:col-span-5",
};
const IMAGE_TILES = ["managed-it"];

const Tile = ({ s, i }) => {
  const big = IMAGE_TILES.includes(s.id);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={LAYOUT[s.id]}
    >
      <Link
        to={`/solutions#${s.id}`}
        data-testid={`solution-card-${s.id}`}
        className={`group relative flex h-full flex-col justify-between overflow-hidden rounded-3xl border p-7 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 sm:p-8 ${
          big ? "min-h-[420px] border-white/10 bg-[#0E2657] text-white hover:shadow-[0_30px_80px_-30px_rgba(23,119,175,0.6)]" : "min-h-[230px] border-slate-200 bg-white hover:border-[#1777AF]/40 hover:shadow-[0_24px_60px_-30px_rgba(15,23,42,0.35)]"
        }`}
      >
        {big && (
          <>
            <img src={s.image} alt={s.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0E2657] via-[#0E2657]/60 to-[#0E2657]/10" />
          </>
        )}
        <div className="relative flex items-start justify-between">
          <span className={`grid h-12 w-12 place-items-center rounded-2xl ${big ? "bg-white/10 text-cyan-300 backdrop-blur-md" : "bg-[#EAF0F7] text-[#1777AF]"}`}>
            <s.icon className="h-5 w-5" />
          </span>
          <span className={`grid h-10 w-10 place-items-center rounded-full border transition-[transform,background-color,color] duration-500 group-hover:rotate-45 ${big ? "border-white/20 group-hover:bg-cyan-300 group-hover:text-[#0E2657]" : "border-slate-200 group-hover:bg-[#1777AF] group-hover:text-white"}`}>
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
        <div className="relative mt-10">
          <p className={`eyebrow ${big ? "text-cyan-300" : "text-slate-500"}`}>{s.kicker}</p>
          <h3 className={`mt-3 font-display font-semibold tracking-tight ${big ? "text-3xl sm:text-4xl" : "text-2xl"}`}>{s.title}</h3>
          <p className={`mt-3 max-w-md text-sm leading-relaxed sm:text-base ${big ? "text-slate-300" : "text-slate-600"}`}>{s.blurb}</p>
        </div>
      </Link>
    </motion.div>
  );
};

export const SolutionsBento = () => (
  <section data-testid="solutions-section" className="bg-[#EAF0F7] py-24 sm:py-32">
    <div className="container-x">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="eyebrow text-[#1777AF]">Solutions & services</p>
          <MaskLines lines={["Three services.", "One affordable team."]} className="mt-5 font-display text-4xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" />
        </div>
        <Link to="/solutions" data-testid="solutions-view-all" className="btn-ghost w-fit border-slate-300 text-slate-900 hover:border-[#1777AF] hover:text-[#1777AF]">
          All solutions <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="mt-14 grid gap-4 lg:auto-rows-[minmax(230px,auto)] lg:grid-cols-12 lg:grid-flow-dense">
        {SOLUTIONS.map((s, i) => <Tile key={s.id} s={s} i={i} />)}
      </div>
    </div>
  </section>
);
